var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

// Copyright (c) 2017 Uber Technologies, Inc.
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import MathArray from './math-array';
import { checkNumber, clamp } from './common';
import Matrix4 from './matrix4';
import Quaternion from './quaternion';
import Vector3 from './vector3';

// Internal constants
var ERR_UNKNOWN_ORDER = 'Unknown Euler angle order';
var ALMOST_ONE = 0.99999;

function validateOrder(value) {
  return value >= 0 && value < 6;
}

function checkOrder(value) {
  if (value < 0 && value >= 6) {
    throw new Error(ERR_UNKNOWN_ORDER);
  }
  return value;
}

var Euler = function (_MathArray) {
  _inherits(Euler, _MathArray);

  _createClass(Euler, [{
    key: 'ELEMENTS',
    get: function get() {
      return 4;
    }
    /* eslint-enable no-multi-spaces, brace-style, no-return-assign */

    /*
     * Number|Number[], Number, Number, Number
     */

  }], [{
    key: 'rotationOrder',
    value: function rotationOrder(order) {
      return Euler.RotationOrders[order];
    }
  }, {
    key: 'ZYX',


    // static XYZ = 0;
    // static YZX = 1;
    // static ZXY = 2;
    // static XZY = 3;
    // static YXZ = 4;
    // static ZYX = 5;
    // static RollPitchYaw = 0;
    // static DefaultOrder = 0;

    // Constants
    /* eslint-disable no-multi-spaces, brace-style, no-return-assign */
    get: function get() {
      return 0;
    }
  }, {
    key: 'YXZ',
    get: function get() {
      return 1;
    }
  }, {
    key: 'XZY',
    get: function get() {
      return 2;
    }
  }, {
    key: 'ZXY',
    get: function get() {
      return 3;
    }
  }, {
    key: 'YZX',
    get: function get() {
      return 4;
    }
  }, {
    key: 'XYZ',
    get: function get() {
      return 5;
    }
  }, {
    key: 'RollPitchYaw',
    get: function get() {
      return 0;
    }
  }, {
    key: 'DefaultOrder',
    get: function get() {
      return Euler.ZYX;
    }
  }, {
    key: 'RotationOrders',
    get: function get() {
      return ['ZYX', 'YXZ', 'XZY', 'ZXY', 'YZX', 'XYZ'];
    }
  }]);

  function Euler() {
    var x = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    var y = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    var z = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
    var order = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : Euler.DefaultOrder;

    _classCallCheck(this, Euler);

    var _this = _possibleConstructorReturn(this, (Euler.__proto__ || Object.getPrototypeOf(Euler)).call(this));

    if (arguments.length > 0 && Array.isArray(arguments[0])) {
      _this.fromVector3.apply(_this, arguments);
    } else {
      _this.set(x, y, z, order);
    }
    return _this;
  }

  // If copied array does contain fourth element, preserves currently set order


  _createClass(Euler, [{
    key: 'copy',
    value: function copy(array) {
      for (var i = 0; i < 3; ++i) {
        this[i] = array[i];
      }
      this[3] = Number.isFinite(array[3]) || this.order;
      this.check();
      return this;
    }

    // Sets the three angles, and optionally sets the rotation order
    // If order is not specified, preserves currently set order

  }, {
    key: 'set',
    value: function set() {
      var x = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      var y = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
      var z = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
      var order = arguments[3];

      this[0] = x;
      this[1] = y;
      this[2] = z;
      this[3] = Number.isFinite(order) ? order : this[3];
      this.check();
      return this;
    }
  }, {
    key: 'validate',
    value: function validate() {
      return validateOrder(this[3]) && Number.isFinite(this[0]) && Number.isFinite(this[1]) && Number.isFinite(this[2]);
    }

    // Does not copy the orientation element

  }, {
    key: 'toArray',
    value: function toArray() {
      var array = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
      var offset = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;

      array[offset] = this[0];
      array[offset + 1] = this[1];
      array[offset + 2] = this[2];
      return array;
    }

    // Copies the orientation element

  }, {
    key: 'toArray4',
    value: function toArray4() {
      var array = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
      var offset = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;

      array[offset] = this[0];
      array[offset + 1] = this[1];
      array[offset + 2] = this[2];
      array[offset + 3] = this[3];
      return array;
    }
  }, {
    key: 'toVector3',
    value: function toVector3(optionalResult) {
      if (optionalResult) {
        return optionalResult.set(this[0], this[1], this[2]);
      }
      return new Vector3(this[0], this[1], this[2]);
    }

    /* eslint-disable no-multi-spaces, brace-style, no-return-assign */
    // x, y, z angle notation (note: only corresponds to axis in XYZ orientation)

  }, {
    key: 'fromVector3',

    /* eslint-disable no-multi-spaces, brace-style, no-return-assign */

    // Constructors
    value: function fromVector3(v, order) {
      return this.set(v[0], v[1], v[2], Number.isFinite(order) ? order : this[3]);
    }

    // TODO - with and without 4th element

  }, {
    key: 'fromArray',
    value: function fromArray(array) {
      var offset = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;

      this[0] = array[0 + offset];
      this[1] = array[1 + offset];
      this[2] = array[2 + offset];
      if (array[3] !== undefined) {
        this[3] = array[3];
      }
      this.check();
      return this;
    }

    // Common ZYX rotation order

  }, {
    key: 'fromRollPitchYaw',
    value: function fromRollPitchYaw(roll, pitch, yaw) {
      return this.set(roll, pitch, yaw, Euler.ZYX);
    }
  }, {
    key: 'fromQuaternion',
    value: function fromQuaternion(q, order) {
      this._fromRotationMatrix(Matrix4.fromQuaternion(q), order);
      this.check();
      return this;
    }
  }, {
    key: 'fromRotationMatrix',
    value: function fromRotationMatrix(m) {
      var order = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : Euler.DefaultOrder;

      this._fromRotationMatrix(m, order);
      this.check();
      return this;
    }

    // ACCESSORS

    // @return {Matrix4} a rotation matrix corresponding to rotations
    //   per the specified euler angles

  }, {
    key: 'getRotationMatrix',
    value: function getRotationMatrix() {
      var m = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : new Matrix4();

      this._getRotationMatrix(m);
      return m;
    }
  }, {
    key: 'getQuaternion',
    value: function getQuaternion() {
      var q = new Quaternion();
      switch (this[4]) {
        case Euler.XYZ:
          return q.rotateX(this[0]).rotateY(this[1]).rotateZ(this[2]);
        case Euler.YXZ:
          return q.rotateY(this[0]).rotateX(this[1]).rotateZ(this[2]);
        case Euler.ZXY:
          return q.rotateZ(this[0]).rotateX(this[1]).rotateY(this[2]);
        case Euler.ZYX:
          return q.rotateZ(this[0]).rotateY(this[1]).rotateX(this[2]);
        case Euler.YZX:
          return q.rotateY(this[0]).rotateZ(this[1]).rotateX(this[2]);
        case Euler.XZY:
          return q.rotateX(this[0]).rotateZ(this[1]).rotateY(this[2]);
        default:
          throw new Error(ERR_UNKNOWN_ORDER);
      }
    }

    // INTERNAL METHODS

    // Concersion from Euler to rotation matrix and from matrix to Euler
    // Adapted from three.js under MIT license

    // // WARNING: this discards revolution information -bhouston
    // reorder(newOrder) {
    //   const q = new Quaternion().setFromEuler(this);
    //   return this.setFromQuaternion(q, newOrder);

    /* eslint-disable complexity, max-statements, one-var */

  }, {
    key: '_fromRotationMatrix',
    value: function _fromRotationMatrix(m) {
      var order = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : Euler.DefaultOrder;

      // assumes the upper 3x3 of m is a pure rotation matrix (i.e, unscaled)

      var te = m.elements;
      var m11 = te[0],
          m12 = te[4],
          m13 = te[8];
      var m21 = te[1],
          m22 = te[5],
          m23 = te[9];
      var m31 = te[2],
          m32 = te[6],
          m33 = te[10];

      order = order || this[3];

      switch (order) {
        case Euler.XYZ:
          this[1] = Math.asin(clamp(m13, -1, 1));

          if (Math.abs(m13) < ALMOST_ONE) {
            this[0] = Math.atan2(-m23, m33);
            this[2] = Math.atan2(-m12, m11);
          } else {
            this[0] = Math.atan2(m32, m22);
            this[2] = 0;
          }
          break;

        case Euler.YXZ:
          this[0] = Math.asin(-clamp(m23, -1, 1));

          if (Math.abs(m23) < ALMOST_ONE) {
            this[1] = Math.atan2(m13, m33);
            this[2] = Math.atan2(m21, m22);
          } else {
            this[1] = Math.atan2(-m31, m11);
            this[2] = 0;
          }
          break;

        case Euler.ZXY:
          this[0] = Math.asin(clamp(m32, -1, 1));

          if (Math.abs(m32) < ALMOST_ONE) {
            this[1] = Math.atan2(-m31, m33);
            this[2] = Math.atan2(-m12, m22);
          } else {
            this[1] = 0;
            this[2] = Math.atan2(m21, m11);
          }
          break;

        case Euler.ZYX:
          this[1] = Math.asin(-clamp(m31, -1, 1));

          if (Math.abs(m31) < ALMOST_ONE) {
            this[0] = Math.atan2(m32, m33);
            this[2] = Math.atan2(m21, m11);
          } else {
            this[0] = 0;
            this[2] = Math.atan2(-m12, m22);
          }
          break;

        case Euler.YZX:
          this[2] = Math.asin(clamp(m21, -1, 1));

          if (Math.abs(m21) < ALMOST_ONE) {
            this[0] = Math.atan2(-m23, m22);
            this[1] = Math.atan2(-m31, m11);
          } else {
            this[0] = 0;
            this[1] = Math.atan2(m13, m33);
          }
          break;

        case Euler.XZY:
          this[2] = Math.asin(-clamp(m12, -1, 1));

          if (Math.abs(m12) < ALMOST_ONE) {
            this[0] = Math.atan2(m32, m22);
            this[1] = Math.atan2(m13, m11);
          } else {
            this[0] = Math.atan2(-m23, m33);
            this[1] = 0;
          }
          break;

        default:
          throw new Error(ERR_UNKNOWN_ORDER);
      }

      this[3] = order;

      return this;
    }
  }, {
    key: '_getRotationMatrix',
    value: function _getRotationMatrix() {
      var te = new Matrix4();

      var x = this.x,
          y = this.y,
          z = this.z;
      var a = Math.cos(x);
      var c = Math.cos(y);
      var e = Math.cos(z);
      var b = Math.sin(x);
      var d = Math.sin(y);
      var f = Math.sin(z);

      switch (this[3]) {
        case Euler.XYZ:
          {
            var ae = a * e,
                af = a * f,
                be = b * e,
                bf = b * f;

            te[0] = c * e;
            te[4] = -c * f;
            te[8] = d;

            te[1] = af + be * d;
            te[5] = ae - bf * d;
            te[9] = -b * c;

            te[2] = bf - ae * d;
            te[6] = be + af * d;
            te[10] = a * c;
            break;
          }

        case Euler.YXZ:
          {
            var ce = c * e,
                cf = c * f,
                de = d * e,
                df = d * f;

            te[0] = ce + df * b;
            te[4] = de * b - cf;
            te[8] = a * d;

            te[1] = a * f;
            te[5] = a * e;
            te[9] = -b;

            te[2] = cf * b - de;
            te[6] = df + ce * b;
            te[10] = a * c;
            break;
          }

        case Euler.ZXY:
          {
            var _ce = c * e,
                _cf = c * f,
                _de = d * e,
                _df = d * f;

            te[0] = _ce - _df * b;
            te[4] = -a * f;
            te[8] = _de + _cf * b;

            te[1] = _cf + _de * b;
            te[5] = a * e;
            te[9] = _df - _ce * b;

            te[2] = -a * d;
            te[6] = b;
            te[10] = a * c;
            break;
          }

        case Euler.ZYX:
          {
            var _ae = a * e,
                _af = a * f,
                _be = b * e,
                _bf = b * f;

            te[0] = c * e;
            te[4] = _be * d - _af;
            te[8] = _ae * d + _bf;

            te[1] = c * f;
            te[5] = _bf * d + _ae;
            te[9] = _af * d - _be;

            te[2] = -d;
            te[6] = b * c;
            te[10] = a * c;
            break;
          }

        case Euler.YZX:
          {
            var ac = a * c,
                ad = a * d,
                bc = b * c,
                bd = b * d;

            te[0] = c * e;
            te[4] = bd - ac * f;
            te[8] = bc * f + ad;

            te[1] = f;
            te[5] = a * e;
            te[9] = -b * e;

            te[2] = -d * e;
            te[6] = ad * f + bc;
            te[10] = ac - bd * f;
            break;
          }

        case Euler.XZY:
          {
            var _ac = a * c,
                _ad = a * d,
                _bc = b * c,
                _bd = b * d;

            te[0] = c * e;
            te[4] = -f;
            te[8] = d * e;

            te[1] = _ac * f + _bd;
            te[5] = a * e;
            te[9] = _ad * f - _bc;

            te[2] = _bc * f - _ad;
            te[6] = b * e;
            te[10] = _bd * f + _ac;
            break;
          }

        default:
          throw new Error(ERR_UNKNOWN_ORDER);
      }

      // last column
      te[3] = 0;
      te[7] = 0;
      te[11] = 0;

      // bottom row
      te[12] = 0;
      te[13] = 0;
      te[14] = 0;
      te[15] = 1;

      return this;
    }
    /* eslint-enable complexity, max-statements, one-var */

  }, {
    key: 'x',
    get: function get() {
      return this[0];
    },
    set: function set(value) {
      return this[0] = checkNumber(value);
    }
  }, {
    key: 'y',
    get: function get() {
      return this[1];
    },
    set: function set(value) {
      return this[1] = checkNumber(value);
    }
  }, {
    key: 'z',
    get: function get() {
      return this[2];
    },
    set: function set(value) {
      return this[2] = checkNumber(value);
    }

    // alpha, beta, gamma angle notation

  }, {
    key: 'alpha',
    get: function get() {
      return this[0];
    },
    set: function set(value) {
      return this[0] = checkNumber(value);
    }
  }, {
    key: 'beta',
    get: function get() {
      return this[1];
    },
    set: function set(value) {
      return this[1] = checkNumber(value);
    }
  }, {
    key: 'gamma',
    get: function get() {
      return this[2];
    },
    set: function set(value) {
      return this[2] = checkNumber(value);
    }

    // phi, theta, psi angle notation

  }, {
    key: 'phi',
    get: function get() {
      return this[0];
    },
    set: function set(value) {
      return this[0] = checkNumber(value);
    }
  }, {
    key: 'theta',
    get: function get() {
      return this[1];
    },
    set: function set(value) {
      return this[1] = checkNumber(value);
    }
  }, {
    key: 'psi',
    get: function get() {
      return this[2];
    },
    set: function set(value) {
      return this[2] = checkNumber(value);
    }

    // roll, pitch, yaw angle notation

  }, {
    key: 'roll',
    get: function get() {
      return this[0];
    },
    set: function set(value) {
      return this[0] = checkNumber(value);
    }
  }, {
    key: 'pitch',
    get: function get() {
      return this[1];
    },
    set: function set(value) {
      return this[1] = checkNumber(value);
    }
  }, {
    key: 'yaw',
    get: function get() {
      return this[2];
    },
    set: function set(value) {
      return this[2] = checkNumber(value);
    }

    // rotation order, in all three angle notations

  }, {
    key: 'order',
    get: function get() {
      return this[3];
    },
    set: function set(value) {
      return this[3] = checkOrder(value);
    }
  }]);

  return Euler;
}(MathArray);

export default Euler;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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


//////////////////
// WEBPACK FOOTER
// ./node_modules/math.gl/dist-es6/euler.js
// module id = 412
// module chunks = 0